import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import CompetitorReviewsState from '@app/model/review/state/CompetitorReviewsState';

if (typeof HighchartsMore === 'function') {
	(HighchartsMore as (highcharts: typeof Highcharts) => void)(Highcharts);
}

const normalizeScore = (score: number | string | undefined | null): number => {
	const numericScore = Number(score ?? 0);
	if (!Number.isFinite(numericScore)) return 0;
	const displayScore = numericScore > 0 && numericScore <= 1 ? numericScore * 10 : numericScore;
	return Math.max(0, Math.min(10, displayScore));
};

const ReviewsCategoryPerformanceComparisonGraph: React.FunctionComponent<{
	mainHotelReviewsState: CompetitorReviewsState,
	competitorReviewsState: CompetitorReviewsState,
}> = ({ mainHotelReviewsState, competitorReviewsState }) => {

	const options: Highcharts.Options = useMemo(() => {
		const mainHotelCategoryStates = mainHotelReviewsState.stateStatisticsForRootCategories;
		const competitorCategoryStates = competitorReviewsState.stateStatisticsForRootCategories;

		// Create a map of competitor categories for easy lookup
		const competitorCategoryMap = new Map(
			competitorCategoryStates.map(cat => [cat.category.id, cat])
		);

		// Build categories and data arrays
		const categories: string[] = [];
		const mainHotelData: number[] = [];
		const competitorData: number[] = [];

		mainHotelCategoryStates.forEach(mainCat => {
			const competitorCat = competitorCategoryMap.get(mainCat.category.id);

			categories.push(mainCat.category.name);
			mainHotelData.push(normalizeScore(mainCat.statistics.score));
			competitorData.push(normalizeScore(competitorCat?.statistics.score ?? 0));
		});

		const graphOptions: Highcharts.Options = {
			chart: {
				polar: true,
				type: 'line',
			},
			title: {
				text: undefined,
			},
			subtitle: {
				text: 'Performance across all categories',
				style: {
					color: '#6b7280',
					fontSize: '14px',
				},
			},
			pane: {
				size: '80%',
			},
			xAxis: {
				categories: categories,
				tickmarkPlacement: 'on',
				lineWidth: 0,
			},
			yAxis: {
				gridLineInterpolation: 'polygon',
				lineWidth: 0,
				min: 0,
				max: 10,
			},
			tooltip: {
				shared: true,
				pointFormat: '<span style="color:{series.color}">{series.name}: <b>{point.y:.1f}/10</b><br/>',
			},
			legend: {
				align: 'center',
				verticalAlign: 'bottom',
				layout: 'horizontal',
			},
			plotOptions: {
				area: {
					fillOpacity: 0.18,
					lineWidth: 2,
					marker: {
						enabled: true,
						radius: 3,
					},
				},
				series: {
					connectEnds: true,
				},
			},
			series: [
				{
					name: 'Your Property',
					type: 'area',
					data: mainHotelData,
					color: '#4285F4',
					fillColor: 'rgba(66, 133, 244, 0.18)',
					pointPlacement: 'on',
					marker: {
						enabled: true,
					},
				},
				{
					name: 'Competitor',
					type: 'area',
					data: competitorData,
					color: '#a855f7',
					fillColor: 'rgba(168, 85, 247, 0.18)',
					pointPlacement: 'on',
					marker: {
						enabled: true,
						symbol: 'diamond',
					},
				},
			],
			credits: {
				enabled: false,
			},
		};

		return graphOptions;
	}, [mainHotelReviewsState.stateStatisticsForCategories, competitorReviewsState.stateStatisticsForCategories]);

	return <div className="h-full w-full flex items-center justify-center p-4">
		<div className="w-full" style={{ height: '100%' }}>
			<HighchartsReact
				highcharts={Highcharts}
				options={options}
				containerProps={{ style: { height: '100%', width: '100%' } }}
			/>
		</div>
	</div>;
};

export default ReviewsCategoryPerformanceComparisonGraph;
